/**
 * PendingActionsTray — la bandeja de "Acciones pendientes" del Centro de mando.
 *
 * Ocupa el lugar del antiguo board "Resultados de ARIA" (los resultados/gráficos
 * ahora van inline en el hilo). Agrega las acciones que ARIA propuso en la
 * conversación y que el usuario aún no ejecutó (status `pending`), para que no se
 * pierdan al hacer scroll. Cada una se puede aprobar, rechazar o **descartar**
 * (ocultar y no volver a ver — persistido por el padre vía safeStorage).
 *
 * Pura presentación: el padre (AIAssistant) deriva los items de `pendingActions`
 * y aporta los handlers. Reusa `PendingActionCard` (la tarjeta de confirmación
 * en dos pasos ya existente).
 */
import { memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ListTodo } from 'lucide-react';
import PendingActionCard from '../v2/PendingActionCard';

function PendingActionsTrayBase({ items = [], onApprove, onReject, onDismiss }) {
  if (items.length === 0) return null;

  return (
    <section className="shrink-0" aria-label="Acciones pendientes">
      <div className="mb-2 flex items-center gap-2">
        <ListTodo className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Acciones pendientes
        </span>
        <span className="rounded-full bg-[var(--cv-accent-soft)] px-1.5 text-[11px] font-semibold text-[var(--cv-accent)]">
          {items.length}
        </span>
      </div>
      <div className="max-h-[38vh] space-y-2 overflow-y-auto pr-1">
        <AnimatePresence initial={false}>
          {items.map(({ msgId, action, status }) => (
            <motion.div
              key={msgId}
              layout
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.2 }}
              className="relative rounded-2xl border p-3 pr-9 backdrop-blur-xl"
              style={{ background: 'var(--cv-glass-bg)', borderColor: 'var(--cv-glass-border)', boxShadow: 'var(--cv-glass-shadow)' }}
            >
              {/* Sólo si el padre sabe persistir el descarte. El panel lateral
                  reusa esta bandeja sin ese almacenamiento (vive en el canvas,
                  por msgId): pintar el aspa allí sería un botón que no hace
                  nada. */}
              {onDismiss && (
                <button
                  type="button"
                  onClick={() => onDismiss(msgId)}
                  aria-label="Descartar"
                  title="Descartar (no volver a ver)"
                  className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[var(--cv-hover-soft)] hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <PendingActionCard
                action={action}
                status={status}
                onApprove={(editedMessage, override) => onApprove?.(msgId, editedMessage, override)}
                onReject={() => onReject?.(msgId)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
}

const PendingActionsTray = memo(PendingActionsTrayBase);
export default PendingActionsTray;
